import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, ChevronRight, ExternalLink } from 'lucide-react';
import type { TranslationKey } from '@telestore/i18n';
import type { OnboardingStepId } from '../lib/api.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { Panel, Pill } from './ui.js';

/**
 * The onboarding checklist (CLAUDE.md §8).
 *
 * It disappears the moment every required step is done, and it never comes
 * back — a permanent checklist on the home screen of a shop that has been
 * running for a year is clutter, not help.
 *
 * Each row links to the screen that finishes it. Nothing here does the work
 * itself: a wizard that owns its own version of "add a product" is a second
 * product form to keep in sync with the first.
 */

const STEPS: Record<
  OnboardingStepId,
  { to: string; labelKey: TranslationKey; hintKey: TranslationKey }
> = {
  accept_policies: {
    to: '/policy',
    labelKey: 'setup.acceptPolicies',
    hintKey: 'setup.acceptPoliciesHint',
  },
  add_product: { to: '/products', labelKey: 'setup.addProduct', hintKey: 'setup.addProductHint' },
  choose_theme: { to: '/settings', labelKey: 'setup.chooseTheme', hintKey: 'setup.chooseThemeHint' },
  connect_bot: { to: '/settings', labelKey: 'setup.connectBot', hintKey: 'setup.connectBotHint' },
  payment_details: {
    to: '/settings',
    labelKey: 'setup.paymentDetails',
    hintKey: 'setup.paymentDetailsHint',
  },
};

export function SetupChecklist() {
  const { shopId, t } = useSession();

  const query = useQuery({
    queryKey: ['onboarding', shopId],
    queryFn: () => api.onboarding(shopId!),
    enabled: Boolean(shopId),
  });

  // Nothing to show while loading, and nothing to show once finished.
  if (!query.data || query.data.complete) return null;

  const { steps, storefrontUrl } = query.data;
  const done = steps.filter((step) => step.done).length;

  return (
    <Panel
      title={t('setup.title')}
      action={
        <Pill tone="brand">
          {done}/{steps.length}
        </Pill>
      }
    >
      <p className="mb-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
        {t('setup.intro')}
      </p>

      <div className="flex flex-col">
        {steps.map((step) => {
          const meta = STEPS[step.id];
          return (
            <Link
              key={step.id}
              to={meta.to}
              className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-[var(--color-surface)]"
            >
              <span
                className="flex size-5 shrink-0 items-center justify-center rounded-full"
                style={{
                  background: step.done ? 'var(--color-brand)' : 'transparent',
                  border: step.done ? 'none' : '1.5px solid var(--color-line)',
                  color: '#fff',
                }}
                aria-hidden="true"
              >
                {step.done ? <Check size={12} strokeWidth={3} /> : null}
              </span>

              <span className="flex-1">
                <span
                  className="block text-sm font-medium"
                  style={{
                    textDecoration: step.done ? 'line-through' : 'none',
                    opacity: step.done ? 0.55 : 1,
                  }}
                >
                  {t(meta.labelKey)}
                </span>
                {!step.done ? (
                  <span className="block text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {t(meta.hintKey)}
                  </span>
                ) : null}
              </span>

              {/*
                Optional steps say so. A merchant selling only COD never needs
                a bot, and marking them "incomplete" forever for declining a
                feature is how a checklist turns into noise they learn to skip.
              */}
              {!step.required && !step.done ? (
                <Pill tone="neutral">{t('common.optional')}</Pill>
              ) : null}

              {!step.done ? (
                <ChevronRight
                  size={16}
                  className="shrink-0 opacity-40"
                  aria-hidden="true"
                />
              ) : null}
            </Link>
          );
        })}
      </div>

      <a
        href={storefrontUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-3 inline-flex items-center gap-1.5 text-sm underline"
        style={{ color: 'var(--color-brand)' }}
      >
        <ExternalLink size={14} aria-hidden="true" />
        {t('setup.viewStorefront')}
      </a>
    </Panel>
  );
}
